/**
 * LiveTrustDashboard — public, no-auth aggregate metrics widget
 *
 * Sits at the TOP of /trust (PublicTrust.js). Pulls real numbers from
 * /api/trust/dashboard so enterprise procurement teams can see the
 * platform is actually being used.
 *
 * Privacy: every number is aggregate. Actor IDs are server-hashed.
 * No emails, no per-customer numbers, no exact timestamps.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, Sparkles, Cloud, Database, ShieldCheck, GitBranch,
  Scissors, Lock, RefreshCw, Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  operational: { color: 'emerald', label: 'All systems operational' },
  degraded_performance: { color: 'amber', label: 'Degraded performance' },
  partial_outage: { color: 'amber', label: 'Partial outage' },
  major_outage: { color: 'rose', label: 'Major outage' },
};

const formatUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `$${(v / 1000).toFixed(2)}K`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
};
const formatN = (n) => Number(n || 0).toLocaleString();

export default function LiveTrustDashboard() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [r, h] = await Promise.all([
        axios.get(`${API}/trust/dashboard`),
        axios.get(`${API}/trust/component-history`).catch(() => ({ data: null })),
      ]);
      setData(r.data);
      if (h.data) setHistory(h.data);
    } catch (e) {
      // graceful — leave previous data on screen
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);  // 30s auto-refresh
    return () => clearInterval(id);
  }, []);

  const statusMeta = data
    ? (STATUS_META[data.system_status] || STATUS_META.operational)
    : { color: 'slate', label: 'Checking…' };

  const statusColor = {
    emerald: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    amber:   'bg-amber-500/10   border-amber-500/40   text-amber-300',
    rose:    'bg-rose-500/10    border-rose-500/40    text-rose-300',
    slate:   'bg-slate-500/10   border-slate-500/30   text-slate-300',
  }[statusMeta.color];

  return (
    <section className="px-4 py-8 sm:py-10 max-w-6xl mx-auto" data-testid="live-trust-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">
          <Activity className="w-3 h-3" /> Live · auto-refresh 30s · /api/trust/dashboard
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-emerald-300 transition-colors disabled:opacity-50"
          data-testid="trust-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Overall status row */}
      <div className={`rounded-2xl border ${statusColor} px-5 py-4 flex items-center justify-between mb-4`} data-testid="trust-overall-status">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Right now</div>
            <div className="text-lg font-bold mt-0.5" data-testid="trust-overall-label">{statusMeta.label}</div>
          </div>
        </div>
        {data?.uptime_30d_pct != null && (
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">30-day uptime</div>
            <div className="text-lg font-bold font-mono tabular-nums" data-testid="trust-uptime-30d">
              {Number(data.uptime_30d_pct).toFixed(3)}%
            </div>
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="trust-kpi-grid">
        <KpiCard
          icon={Activity}
          label="Privileged actions"
          value={loading ? '—' : formatN(data?.audit_events_24h)}
          sub="last 24h · in audit log"
          accent="emerald"
          testid="kpi-actions-24h"
        />
        <KpiCard
          icon={Sparkles}
          label="AI tokens saved"
          value={loading ? '—' : formatN(data?.engine_27?.tokens_saved)}
          sub="by the Auto-Compiler Proxy · all time"
          accent="emerald"
          testid="kpi-tokens-saved"
        />
        <KpiCard
          icon={Database}
          label="Cache hits"
          value={loading ? '—' : formatN(data?.engine_27?.cache_hits)}
          sub="duplicate calls eliminated"
          accent="sky"
          testid="kpi-cache-hits"
        />
        <KpiCard
          icon={GitBranch}
          label="Cascade routings"
          value={loading ? '—' : formatN(data?.engine_27?.cascade_calls)}
          sub="cheap-model-first routes"
          accent="amber"
          testid="kpi-cascade"
        />
      </div>

      {/* Secondary row — savings $ + customer count + subprocessors */}
      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="trust-cumulative-savings">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300">
            <Scissors className="w-3 h-3" /> AI bill cuts (all customers)
          </div>
          <div className="text-2xl font-extrabold text-emerald-300 mt-1 font-mono tabular-nums" data-testid="trust-cumulative-savings-amount">
            {loading ? '—' : formatUsd(data?.engine_27?.cumulative_savings_usd)}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">measured by the proxy itself · zero estimation</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4" data-testid="trust-active-orgs">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
            <Users className="w-3 h-3" /> Active proxy organisations
          </div>
          <div className="text-2xl font-bold text-white mt-1 font-mono tabular-nums">
            {loading ? '—' : formatN(data?.engine_27?.active_proxy_orgs)}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">distinct organisations · last 30 days</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4" data-testid="trust-subprocessor-count">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
            <Cloud className="w-3 h-3" /> Sub-processors
          </div>
          <div className="text-2xl font-bold text-white mt-1 font-mono tabular-nums">
            {loading ? '—' : formatN(data?.subprocessor_count)}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            full list below · DPA-bound · GDPR Art. 28
          </div>
        </div>
      </div>

      {/* Anonymized activity feed */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-900/30 p-4" data-testid="trust-activity-feed">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
            <Activity className="w-3 h-3" /> Recent platform activity · anonymized
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <Lock className="w-3 h-3" /> actor IDs hashed
          </div>
        </div>
        {loading ? (
          <div className="text-xs text-slate-500 text-center py-6">Loading…</div>
        ) : !data?.recent_activity?.length ? (
          <div className="text-xs text-slate-500 text-center py-6" data-testid="trust-feed-empty">
            No recent activity to show. Self-test the audit log from a signed-in account to make this feed live.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.recent_activity.map((evt, i) => (
              <ActivityRow key={i} evt={evt} testid={`trust-feed-row-${i}`} />
            ))}
          </div>
        )}
      </div>

      {/* Sub-processor list (full, public) */}
      <details className="mt-5 group" data-testid="trust-subprocessor-list">
        <summary className="cursor-pointer text-xs text-slate-400 hover:text-emerald-300 select-none flex items-center gap-1.5">
          <Cloud className="w-3 h-3" /> Show full sub-processor list ({data?.subprocessor_count || 7})
        </summary>
        <ul className="mt-2 space-y-1 text-xs text-slate-300 font-mono pl-5 list-disc">
          {(data?.subprocessors || []).map((s, i) => (
            <li key={i} data-testid={`trust-subprocessor-${i}`}>{s}</li>
          ))}
        </ul>
      </details>

      {/* v35.10 — Per-component 24h uptime sparklines */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-900/30 p-4" data-testid="trust-component-history">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
            <Activity className="w-3 h-3" /> 24-hour uptime · per component
          </div>
          <div className="text-[10px] text-slate-500 font-mono">/api/trust/component-history</div>
        </div>
        {!history ? (
          <div className="text-xs text-slate-500 text-center py-4" data-testid="trust-component-history-loading">
            Loading…
          </div>
        ) : (
          <div className="space-y-1.5">
            {(history.components || []).map((c) => (
              <ComponentSparklineRow
                key={c}
                name={c}
                buckets={history.history?.[c] || []}
                uptimePct={history.uptime_24h_pct?.[c]}
                bucketStartsAt={history.bucket_starts_at || []}
                testid={`trust-component-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
              />
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500 font-mono flex-wrap" data-testid="trust-sparkline-legend">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> operational</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-amber-500" /> degraded</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-rose-500" /> down</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-slate-700" /> no data</span>
          <span className="ml-auto">left = 24h ago · right = now</span>
        </div>
      </div>

      {/* Privacy footer */}
      <div className="mt-4 text-[10px] text-slate-500 leading-relaxed" data-testid="trust-privacy-footer">
        <span className="text-emerald-400 font-mono">Privacy guarantees:</span>
        {' '}every actor ID is SHA-256 hashed before leaving the database. Emails never appear in this response.
        Per-customer numbers are never exposed — only platform-wide aggregates. Activity timestamps are relative (&quot;2m ago&quot;) not absolute.
        {' '}This page is publicly cacheable for 30 seconds.
      </div>
    </section>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, testid }) {
  const accentClass = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
    sky:     'border-sky-500/30 bg-sky-500/5 text-sky-300',
    amber:   'border-amber-500/30 bg-amber-500/5 text-amber-300',
  }[accent];
  return (
    <div className={`rounded-xl border ${accentClass} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest opacity-80">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-extrabold mt-1 font-mono tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-400 mt-1">{sub}</div>
    </div>
  );
}

function ActivityRow({ evt, testid }) {
  return (
    <div className="py-2 flex items-center justify-between text-xs" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <code className="text-emerald-300 bg-slate-950 px-1.5 py-0.5 rounded font-mono text-[10px] shrink-0">{evt.actor}</code>
        <span className="text-slate-300 truncate">{evt.action_label}</span>
      </div>
      <span className="text-slate-500 font-mono shrink-0">{evt.rel_time}</span>
    </div>
  );
}

// v35.10 — One row per component on the 24h uptime sparkline strip
// v35.11 — Added hover-tooltip with exact UTC timestamp + relative time
function ComponentSparklineRow({ name, buckets, uptimePct, bucketStartsAt, testid }) {
  const colorFor = (state) => ({
    operational: 'bg-emerald-500',
    degraded:    'bg-amber-500',
    down:        'bg-rose-500',
    no_data:     'bg-slate-700',
  }[state] || 'bg-slate-700');

  const labelFor = (state) => ({
    operational: 'Operational',
    degraded:    'Degraded',
    down:        'Down',
    no_data:     'No data',
  }[state] || state);

  // Pad to 24 buckets (oldest first) — both buckets AND starts
  const padded = [...buckets];
  while (padded.length < 24) padded.unshift('no_data');
  const starts = [...(bucketStartsAt || [])];
  while (starts.length < 24) starts.unshift(null);

  const uptime = uptimePct == null ? null : Number(uptimePct);
  const uptimeColor = uptime == null
    ? 'text-slate-400'
    : uptime >= 99.9 ? 'text-emerald-300'
    : uptime >= 95   ? 'text-amber-300'
    : 'text-rose-300';

  const formatHourRange = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const end = new Date(d.getTime() + 60 * 60 * 1000);
      const fmt = (dt) => dt.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: 'UTC', hour12: false,
      });
      return `${fmt(d)} – ${fmt(end).split(' ').slice(-1)[0]} UTC`;
    } catch (_) { return null; }
  };

  const relativeFromNow = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const h = Math.floor(diffMs / 3_600_000);
      if (h <= 0) return 'this hour';
      if (h === 1) return '1 hour ago';
      return `${h} hours ago`;
    } catch (_) { return null; }
  };

  return (
    <div className="flex items-center gap-3 py-1" data-testid={testid}>
      <div className="text-[11px] text-slate-300 font-mono w-32 shrink-0 truncate">{name}</div>
      <div className="flex items-center gap-[2px] flex-1 min-w-0" data-testid={`${testid}-bars`}>
        {padded.map((state, i) => {
          const iso = starts[i];
          const rangeLabel = formatHourRange(iso);
          const rel = relativeFromNow(iso);
          return (
            <div
              key={i}
              className="relative group/bar flex-1 min-w-0"
              data-testid={`${testid}-bar-${i}`}
              data-state={state}
              data-bucket-iso={iso || ''}
              aria-label={`${name}: ${labelFor(state)}${rangeLabel ? ` · ${rangeLabel}` : ''}`}
            >
              <div className={`h-3.5 rounded-sm ${colorFor(state)} transition-colors hover:ring-2 hover:ring-white/50 cursor-help`} />
              {/* Hover tooltip */}
              <div
                role="tooltip"
                className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-40 opacity-0 group-hover/bar:opacity-100 transition-opacity duration-150"
                data-testid={`${testid}-tooltip-${i}`}
              >
                <div className="rounded-md border border-white/15 bg-slate-950/95 backdrop-blur px-3 py-2 shadow-xl whitespace-nowrap">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-white">
                    <span className={`inline-block w-2 h-2 rounded-sm ${colorFor(state)}`} />
                    {labelFor(state)}
                  </div>
                  {rangeLabel && (
                    <div className="mt-1 text-[10px] font-mono text-slate-300">{rangeLabel}</div>
                  )}
                  {rel && (
                    <div className="text-[10px] font-mono text-slate-500">{rel}</div>
                  )}
                  <div className="mt-1 text-[10px] text-slate-500 truncate max-w-[160px]">{name}</div>
                </div>
                {/* Arrow */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px h-2 w-2 rotate-45 bg-slate-950 border-r border-b border-white/15" />
              </div>
            </div>
          );
        })}
      </div>
      <div className={`text-[11px] font-mono tabular-nums w-14 text-right shrink-0 ${uptimeColor}`} data-testid={`${testid}-uptime`}>
        {uptime == null ? '—' : `${uptime.toFixed(1)}%`}
      </div>
    </div>
  );
}
